import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Select from '../Elements/Select/Select';
import LazyImage from '../../hocs/Lazy/LazyImage';
import { changeCountry, changeAddressInput } from '../../actions/checkout';
import { resetFilter } from '../../actions/environment';
import { addEvent, removeEvent, height } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { translate, prefix } from '../../utils/locale';
import { urlify, cleanInternalLinks } from '../../utils/string';
import s from './Navigation.scss';

const Navigation = ({ navigation, header, footer, location, setup, menuPopupHeight, updateMenuPopupHeight, closeSearch, cartOpen, countryChange, ...props }) => {
	const [navigationItemExpanded, expandNavigationItem] = useState({
		level0: null,
		level1: null,
		level2: null,
	});
	const [hoveringOnLink, updateHoveredLink] = useState('');
	const [hoverHeight, updateHoverHeight] = useState(60);
	const navigationInner = useRef(null);

	useEffect(() => {
		addEvent(document.querySelector('body'), 'click.navigationClose', (e) => {
			if (navigationInner.current && !navigationInner.current.contains(e.target) && Object.keys(navigationItemExpanded).filter((open) => navigationItemExpanded[open] !== null).length > 0) {
				e.preventDefault();
				e.stopPropagation();
				expandNavigationItem({
					level0: null,
					level1: null,
					level2: null,
				});
			}
		});

		addEvent(window, 'keydown.navigationClose', (e) => {
			if (e.key === 'Escape') {
				expandNavigationItem({
					level0: null,
					level1: null,
					level2: null,
				});
			}
		});

		return () => {
			removeEvent(document.querySelector('body'), 'click.navigationClose');
			removeEvent(window, 'keydown.navigationClose');
		};
	});

	const hoveringOnLinkHandler = (e) => {
		if (window.innerWidth < 1100) return;
		const index = e.currentTarget.getAttribute('id').replace('level-', '').split('_');
		if (e.type === 'mouseleave') {
			updateHoveredLink((prevState) => (prevState === index ? '' : prevState));
		} else {
			updateHoveredLink(index);
		}
	};

	const hoveringNavigationHandler = (e) => {
		if (window.innerWidth < 1100) return;
		const $menu = e.currentTarget;
		const index = $menu.children[0].getAttribute('id').replace('level1-', '');
		let setHeight = 60;
		if (e.type === 'mouseenter') {
			setHeight = Math.max(height($menu.querySelector('.u_menuExpand'), 'scrollHeight'), height($menu.querySelector('.u_menuExpand img'), 'scrollHeight'));
			setHeight += 60;
			expandNavigationItem({
				level0: 1,
				level1: parseInt(index, 10),
				level2: null,
			});
		} else {
			expandNavigationItem({
				level0: null,
				level1: null,
				level2: null,
			});
		}
		updateHoverHeight(setHeight);
	};

	const resetFilterHandler = () => {
		props.resetFilter();
		closeSearch();
		updateHoverHeight(60);
		expandNavigationItem({
			level0: null,
			level1: null,
			level2: null,
		});
	};

	const closeMobileNavigationHandler = (e) => {
		e.preventDefault();
		expandNavigationItem({
			level0: null,
			level1: null,
			level2: null,
		});
	};


	// Remove countries not shipped to.
	const countries = Object.keys(setup.countries).reduce((acc, country) => (country !== '0' && setup.countries[country].shipTo ? [...acc, { key: country, value: `${setup.countries[country].name} (${setup.countries[country].currency})` }] : [...acc]), []);

	const expandNavigationHandler = (e) => {
		const id = e.currentTarget.id;
		const level = id.match(/(level\d\w*)/)[1];
		const index = parseInt(id.match(/-(\d+)/)[1], 10);

		if (level === 'level0') {
			// Toggle mobile menu
			e.preventDefault();

			updateMenuPopupHeight();
			expandNavigationItem((prevState) => ({
				...prevState,
				level0: prevState.level0 ? null : 1,
				level1: null,
				level2: null,
			}));
		} else if (navigationItemExpanded[level] !== index && (level === 'level1' || window.innerWidth < 1100)) {
			// Expand level 1
			e.preventDefault();
			expandNavigationItem((prevState) => ({
				...prevState,
				[level]: index,
			}));
		} else if (navigationItemExpanded[level] === index && (level === 'level1' || window.innerWidth < 1100)) {
			// Collapse level 1
			e.preventDefault();
			expandNavigationItem((prevState) => ({
				...prevState,
				[level]: null,
			}));
		} else {
			// Navigate to link
			expandNavigationItem({
				level0: null,
				level1: null,
				level2: null,
			});
		}
	};

	return (
		<nav className={[s.navigation, (cartOpen ? s.navigation__cartOpen : null), (navigationItemExpanded.level0 !== null || navigationItemExpanded.level1 !== null ? s.navigation__expanded : null)].join(' ')} ref={navigationInner}>
			<Link className={s.navigation_logo} to={prefix(setup)} onClick={resetFilterHandler}>
				<svg xmlns="http://www.w3.org/2000/svg" width="1782" height="390" viewBox="0 0 1782 390">
					<path fill="#0B0B0B" d="M203.585,134.45 L203.585,145.74 L160.475,145.74 L160.475,138.21 C160.475,118.03 156.715,101.95 134.815,101.95 C121.135,101.95 112.925,108.1 112.925,125.21 C112.925,142.32 121.825,150.18 136.185,160.45 C144.395,166.27 159.795,176.87 171.425,186.45 C196.395,206.63 207.685,222.37 207.685,252.13 C207.685,299.68 178.605,325 134.135,325 C83.505,325 65.035,292.5 65.035,248.71 L65.035,231.26 L109.505,231.26 L109.505,244.94 C109.505,269.57 115.325,286.34 136.195,286.34 C152.275,286.34 159.805,276.76 159.805,258.29 C159.805,236.4 148.515,228.52 138.935,221 C121.835,207.66 114.985,203.89 102.335,193.63 C78.725,174.47 65.045,158.05 65.045,128.29 C65.045,89.63 87.965,65 134.495,65 C203.605,65 203.605,120.42 203.605,134.45 L203.585,134.45 Z M218.975,108.11 L257.635,108.11 L257.635,322.27 L303.135,322.27 L303.135,108.1 L341.455,108.1 L341.455,67.73 L218.985,67.73 L218.985,108.1 L218.975,108.11 Z M365.735,262.4 C365.735,299.35 382.155,325 427.655,325 L435.185,325 C480.685,325 497.445,299.34 497.445,262.4 L497.445,67.74 L451.605,67.74 L451.605,261.71 C451.605,276.42 446.135,285.32 431.425,285.32 C416.715,285.32 411.585,276.42 411.585,261.71 L411.585,67.74 L365.745,67.74 L365.745,262.4 L365.735,262.4 Z M522.425,108.11 L561.085,108.11 L561.085,322.27 L606.585,322.27 L606.585,108.1 L644.905,108.1 L644.905,67.73 L522.435,67.73 L522.435,108.1 L522.425,108.11 Z M664.745,108.11 L703.405,108.11 L703.405,322.27 L748.905,322.27 L748.905,108.1 L787.225,108.1 L787.225,67.73 L664.755,67.73 L664.755,108.1 L664.745,108.11 Z M812.535,322.27 L926.455,322.27 L926.455,281.56 L857.005,281.56 L857.005,207.32 L918.585,207.32 L918.585,166.61 L857.005,166.61 L857.005,107.43 L924.405,107.43 L924.405,67.74 L812.535,67.74 L812.535,322.27 L812.535,322.27 Z M1088.735,139.24 L1088.735,115.98 C1088.735,82.45 1072.315,67.74 1039.475,67.74 L956.005,67.74 L956.005,322.27 L1000.825,322.27 L1000.825,204.93 L1023.065,204.93 C1038.115,204.93 1043.935,212.46 1043.935,229.9 L1043.935,322.27 L1088.755,322.27 L1088.755,222.38 C1088.755,196.38 1076.435,185.43 1063.095,180.3 C1074.725,176.88 1088.755,165.93 1088.755,139.25 L1088.735,139.24 Z M1043.915,143.35 C1043.915,159.77 1038.445,168.66 1023.045,168.66 L1000.805,168.66 L1000.805,101.95 L1023.045,101.95 C1038.435,101.95 1043.915,108.45 1043.915,125.9 L1043.915,143.35 Z M1124.775,322.27 L1170.275,322.27 L1170.275,208.35 L1219.195,208.35 L1219.195,322.27 L1264.695,322.27 L1264.695,67.74 L1219.195,67.74 L1219.195,165.58 L1170.275,165.58 L1170.275,67.74 L1124.775,67.74 L1124.775,322.27 Z M1299.935,322.27 L1413.855,322.27 L1413.855,281.56 L1344.405,281.56 L1344.405,207.32 L1405.985,207.32 L1405.985,166.61 L1344.405,166.61 L1344.405,107.43 L1411.805,107.43 L1411.805,67.74 L1299.935,67.74 L1299.935,322.27 Z M1445.675,322.27 L1491.175,322.27 L1491.175,67.74 L1445.675,67.74 L1445.675,322.27 Z M1583.895,67.69 L1621.865,237.03 L1659.495,67.69 L1716.965,67.69 L1716.965,322.22 L1675.225,322.22 L1675.225,162.8 L1638.625,322.22 L1604.755,322.22 L1568.145,162.8 L1568.145,322.22 L1526.405,322.22 L1526.405,67.68 L1583.875,67.68 L1583.895,67.69 Z" />
				</svg>
			</Link>
			<a id="level0-0" className={s.mobile} href="#open-mobile-menu" aria-label="Open mobile menu" onClick={expandNavigationHandler}>
				<span /><span /><span />
			</a>
			<div className={s.navigation_overflow} style={{ height: menuPopupHeight - 9 }}>
				<div className={[s.level1, (navigationItemExpanded.level1 !== null ? s.level1__expanded : null)].join(' ')}>
					<div className={s.level1_expander} style={{ height: `${hoverHeight}px` }}>
						{
							navigation.header && navigation.header.map((level1, i) => {
								const level1URI = translate(setup, level1, 'uri');
								const combinedURILevel1 = prefix(setup, level1URI);
								const styles1 = level1?.styles || {};
								let hoverImage = null;

								if (hoveringOnLink && hoveringOnLink.length > 1) {
									if (level1?.subPages?.[hoveringOnLink[0]]?.subPages?.[hoveringOnLink[1]]?.icon) hoverImage = <LazyImage {...imageSizeTranslation('', level1.subPages[hoveringOnLink[0]].subPages[hoveringOnLink[1]].icon, '-', true, false, '800')} />;
									else if (level1?.subPages?.[hoveringOnLink[0]]?.icon) hoverImage = <LazyImage {...imageSizeTranslation('', level1.subPages[hoveringOnLink[0]].icon, '-', true, false, '800')} />;
									else hoverImage = <LazyImage {...imageSizeTranslation('', level1.icon, '-', true, false, '800')} />;
								} else if (hoveringOnLink && hoveringOnLink.length === 1) {
									if (level1?.subPages?.[hoveringOnLink[0]]?.icon) hoverImage = <LazyImage {...imageSizeTranslation('', level1.subPages[hoveringOnLink[0]].icon, '-', true, false, '800')} />;
									else hoverImage = <LazyImage {...imageSizeTranslation('', level1.icon, '-', true, false, '800')} />;
								} else {
									hoverImage = <LazyImage {...imageSizeTranslation('', level1.icon, '-', true, false, '800')} />;
								}

								return (
									<div key={urlify(combinedURILevel1)} className={[s.level1_expander_block, s[`level1_expander_block__${level1.type}`], (level1.subPages ? s.level1_expander_block__expandable : null), (navigationItemExpanded.level1 === i ? s.level1_expander_block__expanded : null)].join(' ')} onMouseEnter={hoveringNavigationHandler} onMouseLeave={hoveringNavigationHandler}>
										<Link id={`level1-${i}`} className={s.level1_expander_block_link} to={combinedURILevel1} onClick={level1.subPages ? expandNavigationHandler : resetFilterHandler} style={styles1}>
											<span>{ translate(setup, level1, 'title') }</span>
										</Link>
										{
											level1.subPages && (
												<div className={[s.level1_expander_block_inner, 'u_menuExpand'].join(' ')}>
													<div className={s.level2}>
														{
															level1.subPages.map((level2, j) => (
																<div className={s.level2_inner}>
																	<div className={s.level3}>
																		{
																			translate(setup, level2, 'title').indexOf('<<<') === -1 && (
																				<div className={[s.level3_item, s.level3_item__heading].join(' ')} key={urlify(translate(setup, level2, 'uri'))}>
																					<Link id={`level-${j}`} className={s.level3_item_link} to={prefix(setup, translate(setup, level2, 'uri'))} onClick={resetFilterHandler} onMouseEnter={hoveringOnLinkHandler} onMouseLeave={hoveringOnLinkHandler} style={level2?.styles || {}}>
																						<span>{translate(setup, level2, 'title')}</span>
																					</Link>
																				</div>
																			)
																		}
																		{
																			level2.subPages && level2.subPages.map((level3, k) => {
																				const level3URI = translate(setup, level3, 'uri');
																				const styles3 = level3?.styles || {};
																				const level3Label = translate(setup, level3, 'metaFields');

																				if (level3.title && level3.title.indexOf('<<<') !== -1) {
																					return (
																						<div key={urlify(`${level3.title}-${j}`)} className={s.level3_block}>
																							<div className={s.level3_item_spacer} />
																						</div>
																					);
																				}

																				return (
																					<div className={s.level3_item} key={urlify(level3URI)}>
																						<Link id={`level-${j}_${k}`} className={s.level3_item_link} to={prefix(setup, level3URI)} onClick={resetFilterHandler} onMouseEnter={hoveringOnLinkHandler} onMouseLeave={hoveringOnLinkHandler} style={styles3}>
																							<span>{translate(setup, level3, 'title')}</span>
																							{ level3Label && <span className={s.level3_item_link_label}>{ level3Label }</span> }
																						</Link>
																					</div>
																				);
																			})
																		}
																	</div>
																</div>
															))
														}
													</div>
													<div className={s.image}>
														{ hoverImage }
													</div>
												</div>
											)
										}
									</div>
								);
							})
						}
					</div>
					<div className={s.mobileFooterSpacer} />
					<div className={s.mobileFooter}>
						<div className={s.mobileFooter_links}>
							{
								navigation.contact && navigation.contact.map((link) => {
									const uri = cleanInternalLinks(translate(setup, link, 'uri'));
									if (/(http|mailto|tel)/.test(uri)) {
										return <a className={s.mobileFooter_link} href={uri} key={uri} target="_blank" rel="noopener noreferrer nofollow"><span>{ translate(setup, link, 'title') }</span></a>;
									}
									return <Link className={s.mobileFooter_link} onClick={resetFilterHandler} to={prefix(setup, uri)} key={uri}><span>{ translate(setup, link, 'title') }</span></Link>;
								})
							}
						</div>
						<div className={s.mobileFooter_country}>
							<Select id="countryNavigationMobile" variantClasses={['select__country', 'label__country']} selected={setup.country} options={countries} inputChange={countryChange} label={`${translate(setup, 'labelShippingTo')}: `} />
						</div>
					</div>
				</div>
			</div>
			<button className={s.navigation_screen} type="button" onClick={closeMobileNavigationHandler} aria-label="Close mobile menu popup" />
		</nav>
	);
};

const mapState = ({ environment, location }, props) => ({ ...props, header: environment.header, footer: environment.footer, location });

export default hot(module)(connect(mapState, { resetFilter, changeCountry, changeAddressInput })(Navigation));
